import { useState } from 'react';

import { toastService } from '@thefarmersfront/kitchen';

import { registerMockAPI } from '~/mocks/mockApis';

import { useAPI, useAsyncHandler } from '~/hooks/common';
import { addIf, formatDate } from '~/utils';

import { API_ENDPOINT, STOCK_KEYWORD_TYPE_FILTER_KEY, TOAST_MESSAGE } from '~/constants';

import type { TypeErrorResponseData } from '~/types/common.types';
import type {
  TypeGetVendorReleaseParams,
  TypeGetVendorReleaseResponse,
  TypeStockReleaseFilter,
  TypeStockReleaseInfo,
} from '~/types/stock.types';

/**
 * [재고관리] 반출 요청  api 요청 훅
 */
const usePostVendorReleaseSearch = () => {
  const { runAsync } = useAsyncHandler();

  const [responseData, setResponseData] = useState<TypeGetVendorReleaseResponse | null>(null);

  const postVendorReleaseSearch = useAPI<TypeGetVendorReleaseParams, TypeGetVendorReleaseResponse>({
    endpoint: API_ENDPOINT.STOCK.POST_VENDOR_RELEASE_SEARCH,
    method: 'post',
  });

  const requestVendorRelease = async (
    filterState: TypeStockReleaseFilter,
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (data: TypeGetVendorReleaseResponse) => void;
      onError?: (error: TypeErrorResponseData['error']) => void;
    } = {}
  ) => {
    const { cluster, status, method, expectedDate, createdDate, keywordType, keywordValue, pageSize, currentPage } =
      filterState;
    const keywordFilterKey =
      STOCK_KEYWORD_TYPE_FILTER_KEY[keywordType?.value as keyof typeof STOCK_KEYWORD_TYPE_FILTER_KEY];

    const params: TypeGetVendorReleaseParams = {
      ...addIf(cluster.value, 'clusterCode'),
      ...addIf(status.value, 'status'),
      ...addIf(method.value, 'method'),
      expectedDateFrom: formatDate(expectedDate?.start, 'YYYY-MM-DD') ?? '',
      expectedDateTo: formatDate(expectedDate?.end, 'YYYY-MM-DD') ?? '',
      ...addIf(formatDate(createdDate?.start, 'YYYY-MM-DD'), 'createdDateFrom'),
      ...addIf(formatDate(createdDate?.end, 'YYYY-MM-DD'), 'createdDateTo'),
      ...addIf(keywordFilterKey && keywordValue, keywordFilterKey, keywordValue),
      cursor: currentPage,
      limit: pageSize,
    };

    runAsync(() => postVendorReleaseSearch({ params }), {
      onSuccess: (res) => {
        setResponseData(res);
        onSuccess?.(res);
      },
      onError: (error: TypeErrorResponseData['error']) => {
        toastService.addToast({
          type: 'error',
          message: error.message ?? TOAST_MESSAGE.DEFAULT_ERROR,
        });
        onError?.(error);
      },
    });
  };

  return { requestVendorRelease, responseData };
};

export default usePostVendorReleaseSearch;

const mockStockRelease = (index: number, overrides: Partial<TypeStockReleaseInfo> = {}): TypeStockReleaseInfo => ({
  id: 5000 + index,
  statusCode: 'REQUEST',
  statusCodeObject: { code: 'REQUEST', name: '요청' },
  clusterCode: 'CC02',
  clusterCodeObject: { code: 'CC02', name: '김포' },
  methodCode: 'PICKUP',
  methodCodeObject: { code: 'PICKUP', name: '방문수령' },
  expectedDate: '2026-05-21',
  skuCount: 3,
  totalRequestQuantity: 120,
  totalActualQuantity: 0,
  createdAt: '2026-05-18T10:17:33',
  ...overrides,
});

const vendorReleaseNormalResponse: TypeGetVendorReleaseResponse = {
  data: [
    mockStockRelease(1),
    mockStockRelease(2, {
      statusCode: 'PROGRESS',
      statusCodeObject: { code: 'PROGRESS', name: '진행' },
      methodCode: 'TRUCK',
      methodCodeObject: { code: 'TRUCK', name: '화물' },
      totalRequestQuantity: 12000,
    }),
    mockStockRelease(3, {
      statusCode: 'COMPLETE',
      statusCodeObject: { code: 'COMPLETE', name: '완료' },
      clusterCode: 'CC03',
      clusterCodeObject: { code: 'CC03', name: '평택' },
      totalActualQuantity: 120,
    }),
    mockStockRelease(4, {
      statusCode: 'CANCEL',
      statusCodeObject: { code: 'CANCEL', name: '취소' },
      methodCode: 'PARCEL',
      methodCodeObject: { code: 'PARCEL', name: '택배' },
      expectedDate: '2026-05-25',
    }),
    mockStockRelease(5, { skuCount: 1, totalRequestQuantity: 5 }),
  ],
  pagination: { listView: 100, listTotal: 5, cursor: 0 },
};

registerMockAPI<TypeGetVendorReleaseResponse>({
  method: 'post',
  endpoint: API_ENDPOINT.STOCK.POST_VENDOR_RELEASE_SEARCH,
  label: '재고/반출요청관리/반출목록',
  scenarios: [
    { name: '정상 응답', response: vendorReleaseNormalResponse },
    { name: '검색 결과 0건', response: { data: [], pagination: { listView: 100, listTotal: 0, cursor: 0 } } },
  ],
});
